import type { ReactElement } from 'react';
import type { AnimationLight, AnimationSummary } from '@wwf/sdk';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import { useAnimationDrawer } from '@app/animation/drawer/AnimationDrawer';
import { AnimationAvatar } from './AnimationAvatar';
import { MissionTypeChipFromId } from '@app/mission/chips/MissionTypeChip';

export const AnimationLightCard = (props: {
    record?: AnimationSummary | AnimationLight;
    compressed?: boolean;
    noLink?: boolean;
    forceAvailable?: boolean;
}) => {
    const record = useRecordContext<AnimationSummary | AnimationLight>(props);
    const { openDrawer } = useAnimationDrawer();

    const isUnavailableForAntenna =
        (record as AnimationLight)?.available === false &&
        !props.forceAvailable;

    const content = (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={
                isUnavailableForAntenna
                    ? { color: 'text.disabled', opacity: 0.5 }
                    : undefined
            }
        >
            <AnimationAvatar variant="sm" record={record} />
            {record?.missionType && (
                <MissionTypeChipFromId
                    id={record.missionType}
                    compressed={props.compressed}
                />
            )}
            <Typography variant="body2" noWrap>
                {record?.name}
            </Typography>
        </Box>
    );

    const wrapUnavailable = (node: ReactElement) =>
        isUnavailableForAntenna ? (
            <Tooltip
                title="This animation is not available for this antenna."
                placement="top"
            >
                {node}
            </Tooltip>
        ) : (
            node
        );

    if (props.noLink) return wrapUnavailable(content);

    if (record?.id != null) {
        return wrapUnavailable(
            <Link
                component="button"
                variant="body2"
                onClick={() => openDrawer('show', record.id)}
                sx={{
                    cursor: 'pointer',
                    textAlign: 'left',
                    border: 0,
                    background: 'none',
                    padding: 0,
                    font: 'inherit',
                }}
            >
                {content}
            </Link>
        );
    }

    return wrapUnavailable(content);
};
